import React from 'react';
import { useTranslation } from 'react-i18next';
import TranslationNamespaces from '../../locales/TranslationNamespaces';
import { useTheme } from '@mui/material';
import { Phone } from '@mui/icons-material';
import { QButton, QConfirm, QInputForm } from '@kubiq-base/core';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../state/AppContext';
import { useApi } from '../../state/ApiContext';
import { useCache } from '../../state/CachingContext';
import SiteHelper from '../../helpers/SiteHelper';
import HeaderWithDescription from '../Shared/HeaderWithDescription';
import FlexContainer from '../Shared/FlexContainers/FlexContainer';
import FlexFormContainer from '../Shared/FlexContainers/FlexFormContainer';
import CenteredQButton from '../Shared/CenteredQButton';
import MessageBox from '../Shared/MessageBoxes/MessageBox';
import { formatPhoneNumberIntl } from 'react-phone-number-input';

const VerifyPhone = (): JSX.Element => {
  const namespace = TranslationNamespaces.verifyPhone;
  const { t } = useTranslation(namespace);
  const theme = useTheme();
  const { setEmail } = useAppContext();
  const { postRequest, getRequest } = useApi();
  const { getCache, setCache, clearCache } = useCache();
  const session = getCache();
  const { control, handleSubmit } = useForm<{ code: string }>();
  const navigate = useNavigate();
  const [currentNum, setCurrentNum] = React.useState('unavailable');

  const [isSendingSMS, setIsSendingSMS] = React.useState(false);
  const [SMSSent, setSMSSent] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [verifyComplete, setVerifyComplete] = React.useState(false);
  const [openSkip, setOpenSkip] = React.useState(false);

  const sendSMS = async (): Promise<void> => {
    setIsSendingSMS(true);
    await postRequest('Sms/SendVerificationSms', {})
      .then(() => setSMSSent(true))
      .catch(() => setIsSendingSMS(false));
  };

  const redirect = (): void => {
    SiteHelper.handleRedirectExternal(getCache(), getCache('authData'), setEmail, clearCache, setCache, navigate, true);
  };

  const onSubmit = async (data: { code: string }): Promise<void> => {
    setIsSubmitting(true);

    await postRequest('Sms/Verify', { pincode: data.code, identifier: session.email })
      .then(() => setVerifyComplete(true))
      .catch(() => setIsSubmitting(false));
  };

  const bootstrapAsync = async (): Promise<void> => {
    await getRequest<{ data: string }>('User/PhoneNumber', undefined).then((result) => {
      if (result.data) setCurrentNum(formatPhoneNumberIntl(result.data));
    });
  };

  React.useEffect(() => {
    bootstrapAsync();
  }, []);

  return (
    <>
      {!verifyComplete ? (
        <>
          <HeaderWithDescription translationNamespace={namespace} description='descriptionVerifyPhone'>
            <MessageBox text={currentNum} icon={<Phone />} />
          </HeaderWithDescription>

          {!SMSSent ? (
            <FlexContainer>
              <CenteredQButton onClick={() => sendSMS()} fullWidth color='secondary' isLoading={isSendingSMS}>
                {t('sendSMS')}
              </CenteredQButton>
            </FlexContainer>
          ) : (
            <FlexFormContainer onSubmit={handleSubmit(onSubmit)}>
              <QInputForm control={control} name='code' label={t('smsCode')} />

              <CenteredQButton type='submit' fullWidth color='secondary' isLoading={isSubmitting}>
                {t('verify')}
              </CenteredQButton>
            </FlexFormContainer>
          )}

          <div style={{ position: 'absolute', bottom: theme.spacing(1), right: theme.spacing(1) }}>
            <QButton variant='text' onClick={() => setOpenSkip(true)}>
              {t('skip')}
            </QButton>
          </div>

          <QConfirm
            open={openSkip}
            onClose={() => setOpenSkip(false)}
            onConfirm={() => redirect()}
            title={t('youSure')}
            message={t('skipVerify')}
            confirmLabel={t('accept')}
          />
        </>
      ) : (
        <>
          <HeaderWithDescription
            translationNamespace={namespace}
            headerSuffix='Complete'
            description='descriptionVerifyComplete'
          />
          <FlexContainer>
            <CenteredQButton onClick={() => redirect()} fullWidth color='secondary'>
              {t('login')}
            </CenteredQButton>
          </FlexContainer>
        </>
      )}
    </>
  );
};

export default VerifyPhone;
